import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { X, Bug, Lightbulb, Heart, CircleEllipsis } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const HIDDEN_ROUTES = ['/login', '/forgot-password', '/reset-password', '/verify-email', '/privacy', '/terms', '/onboarding'];

const TYPES = [
  { value: 'bug', label: 'Bug', icon: Bug, hint: 'Something broke or behaved unexpectedly' },
  { value: 'suggestion', label: 'Suggestion', icon: Lightbulb, hint: 'An idea to make this better' },
  { value: 'praise', label: 'Praise', icon: Heart, hint: 'Something you loved' },
  { value: 'other', label: 'Other', icon: CircleEllipsis, hint: 'Anything else on your mind' },
];

/**
 * Modal-only feedback widget. Triggered globally by a `window` custom event
 * (`window.dispatchEvent(new Event('open-feedback'))`). The trigger lives in
 * the sidebar user-menu so nothing floats over page content.
 */
export default function BetaFeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-feedback', handler);

    // Keyboard shortcut: press "?" anywhere (outside input/textarea) to open feedback.
    const onKey = (e) => {
      if (e.key !== '?') return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || t?.isContentEditable;
      if (isTyping) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('open-feedback', handler);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const isHidden = HIDDEN_ROUTES.some(r => location.pathname.startsWith(r));
  if (!user || isHidden) return null;

  const submit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        feedback_type: type,
        message: message.trim(),
        page: location.pathname,
        user_agent: navigator.userAgent,
      });
      toast.success('Thank you — your voice has been received.');
      setMessage('');
      setType('suggestion');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="beta-feedback-modal"
      style={{ background: 'rgba(15,16,16,0.55)', backdropFilter: 'blur(8px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-bone rounded-t-3xl sm:rounded-3xl p-7 sm:p-8 w-full sm:max-w-lg relative animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
      >
        <button
          onClick={() => setOpen(false)}
          data-testid="beta-feedback-close"
          className="absolute top-4 right-4 text-text-muted hover:text-dark p-1"
        >
          <X size={20} />
        </button>

        <p className="text-gold font-sans text-[10px] tracking-[0.35em] uppercase mb-2">Beta Feedback</p>
        <h3 className="font-serif text-2xl sm:text-3xl font-light text-dark leading-tight mb-2 mystical-heading">
          Your voice shapes this practice
        </h3>
        <p className="font-sans text-sm text-text-muted leading-relaxed mb-6">
          Bugs, ideas, praise — all welcome. You're co-creating this with us.
        </p>

        <div className="grid grid-cols-2 gap-2 mb-5" data-testid="feedback-type-selector">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = type === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                data-testid={`feedback-type-${t.value}`}
                className={`p-3 rounded-xl border text-left ${
                  active ? 'border-gold/50 bg-gold/10' : 'border-black/8 bg-sand/40 hover:border-gold/25'
                }`}
                style={{ transition: 'border-color 0.2s ease, background-color 0.2s ease' }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className={active ? 'text-gold' : 'text-text-muted'} />
                  <span className={`font-sans text-xs tracking-wider uppercase ${active ? 'text-dark' : 'text-text-muted'}`}>
                    {t.label}
                  </span>
                </div>
                <p className="font-sans text-[11px] text-text-muted/80 leading-snug">{t.hint}</p>
              </button>
            );
          })}
        </div>

        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="feedback-message-input"
          placeholder="What did you notice? Where did it happen? The more specific, the better."
          rows={5}
          maxLength={5000}
          autoFocus
          className="bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans text-base leading-relaxed resize-none mb-2"
        />
        <p className="font-sans text-[11px] text-text-muted text-right mb-5">
          {message.length}/5000
        </p>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            data-testid="feedback-cancel-button"
            className="font-sans text-xs text-text-muted h-11"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !message.trim()}
            data-testid="feedback-submit-button"
            className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase btn-hover h-11 px-6"
          >
            {submitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </div>

        <p className="font-sans text-[10px] text-text-muted/70 text-center mt-5 leading-relaxed">
          We'll see it with your name and email. Sent with care.
          <span className="hidden sm:inline">{' '}· Press <kbd className="px-1.5 py-0.5 rounded bg-bone border border-black/10 font-sans text-[10px] text-dark">?</kbd> anytime to open this.</span>
        </p>
      </div>
    </div>
  );
}
